import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	IconButton,
	Box,
	Chip,
	CircularProgress,
	TextField,
	InputAdornment,
	Grid,
	Card,
	CardContent,
	CardHeader,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PieChartIcon from "@mui/icons-material/PieChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	Legend,
	ResponsiveContainer,
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
} from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import SchoolIcon from "@mui/icons-material/School";
import * as XLSX from "xlsx";
import { Button } from "@mui/material";

// Define colors for the pie chart
const COLORS = [
	"#0088FE",
	"#00C49F",
	"#FFBB28",
	"#FF8042",
	"#8884d8",
	"#82ca9d",
	"#ffc658",
	"#8dd1e1",
];

const ExpenseList = () => {
	const { supabase } = useAuth();
	const [expenses, setExpenses] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState("");
	const [filteredExpenses, setFilteredExpenses] = useState([]);

	// Date range state
	const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
	const [endDate, setEndDate] = useState(dayjs());

	// Analytics state
	const [dateRangeExpenses, setDateRangeExpenses] = useState([]);
	const [categoryData, setCategoryData] = useState([]);
	const [dailyAverageData, setDailyAverageData] = useState([]);
	const [travelStats, setTravelStats] = useState({
		totalDistance: 0,
		totalCost: 0,
	});

	// Period filter state
	const [periodFilter, setPeriodFilter] = useState("month");

	// User filter state
	const [userFilter, setUserFilter] = useState("");

	// School filter state
	const [schoolFilter, setSchoolFilter] = useState("");
	const [schools, setSchools] = useState([]);

	const exportToExcel = () => {
		// Create a worksheet from the filtered expenses data
		const worksheet = XLSX.utils.json_to_sheet(
			filteredExpenses.map((expense) => ({
				Date: dayjs(expense.date).format("DD MMM YYYY"),
				User: expense.user_full_name,
				Email: expense.user_email,
				School: expense.school_name || "N/A",
				Description: expense.description,
				Type: expense.expense_type,
				Distance: expense.distance ? `${expense.distance} km` : "",
				Amount: parseFloat(expense.amount || 0).toFixed(2),
				Receipt: expense.receipt_url ? "Yes" : "No",
			}))
		);

		// Set column widths for better readability
		const columnWidths = [
			{ wch: 12 }, // Date
			{ wch: 20 }, // User
			{ wch: 25 }, // Email
			{ wch: 25 }, // School
			{ wch: 30 }, // Description
			{ wch: 15 }, // Type
			{ wch: 10 }, // Distance
			{ wch: 10 }, // Amount
			{ wch: 10 }, // Receipt
		];
		worksheet["!cols"] = columnWidths;

		// Create a workbook and add the worksheet
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Expenses");

		// Generate Excel file and trigger download
		XLSX.writeFile(
			workbook,
			`Expense_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`
		);
	};

	// Fetch all schools
	useEffect(() => {
		const fetchSchools = async () => {
			try {
				const { data, error } = await supabase
					.from("schools")
					.select("id, name")
					.eq("status", "Active")
					.order("name");

				if (error) throw error;
				setSchools(data || []);
			} catch (error) {
				console.error("Error fetching schools:", error);
			}
		};

		fetchSchools();
	}, []);

	useEffect(() => {
		if (expenses.length === 0) return;

		filterExpensesByDateRangeAndFilters();
	}, [startDate, endDate, expenses, userFilter, schoolFilter]);

	const filterExpensesByDateRangeAndFilters = () => {
		const startTimestamp = startDate.startOf("day");
		const endTimestamp = endDate.endOf("day");

		const filtered = expenses.filter((expense) => {
			const expenseDate = dayjs(expense.date);
			const dateInRange =
				expenseDate.isAfter(startTimestamp) &&
				expenseDate.isBefore(endTimestamp);

			// Apply user filter if selected
			const userMatch = !userFilter || expense.user_full_name === userFilter;

			// Apply school filter if selected - check both direct school_id and school from visit
			let schoolMatch = true;
			if (schoolFilter) {
				// Check if either direct school_id matches OR school_id from visit matches
				schoolMatch =
					expense.school_id === schoolFilter ||
					(expense.school_visits &&
						expense.school_visits.schools &&
						expense.school_visits.schools.id === schoolFilter);
			}

			return dateInRange && userMatch && schoolMatch;
		});

		setDateRangeExpenses(filtered);

		// Force recalculation of analytics when filters change
		calculateCategoryBreakdown(filtered);
		calculateDailyAverage(filtered);
		calculateTravelStats(filtered);
	};

	const handleUserFilterChange = (event) => {
		setUserFilter(event.target.value);
	};

	// Handle school filter change
	const handleSchoolFilterChange = (event) => {
		setSchoolFilter(event.target.value);
	};

	const getUserSpendingData = () => {
		const userSpending = {};

		dateRangeExpenses.forEach((expense) => {
			const userName = expense.user_full_name || "Unknown User";
			if (!userSpending[userName]) {
				userSpending[userName] = 0;
			}
			userSpending[userName] += parseFloat(expense.amount) || 0;
		});

		return Object.keys(userSpending)
			.map((name) => ({ name, amount: userSpending[name] }))
			.sort((a, b) => b.amount - a.amount); // Sort by amount descending
	};

	const getSchoolSpendingData = () => {
		const schoolSpending = {};

		dateRangeExpenses.forEach((expense) => {
			const schoolName = expense.school_name || "No School";
			if (!schoolSpending[schoolName]) {
				schoolSpending[schoolName] = 0;
			}
			schoolSpending[schoolName] += parseFloat(expense.amount) || 0;
		});

		return Object.keys(schoolSpending)
			.map((name) => ({ name, amount: schoolSpending[name] }))
			.sort((a, b) => b.amount - a.amount); // Sort by amount descending
	};

	const getUniqueUsers = () => {
		const users = expenses.map((expense) => expense.user_full_name);
		return ["", ...new Set(users)].filter(Boolean); // Include empty option for "All Users"
	};

	useEffect(() => {
		fetchAllExpenses();
	}, []);

	useEffect(() => {
		// Filter expenses based on search term
		if (searchTerm.trim() === "") {
			setFilteredExpenses(expenses);
		} else {
			const lowercasedSearch = searchTerm.toLowerCase();
			const filtered = expenses.filter(
				(expense) =>
					expense.description?.toLowerCase().includes(lowercasedSearch) ||
					expense.expense_type?.toLowerCase().includes(lowercasedSearch) ||
					expense.user_full_name?.toLowerCase().includes(lowercasedSearch) ||
					expense.user_email?.toLowerCase().includes(lowercasedSearch) ||
					expense.school_name?.toLowerCase().includes(lowercasedSearch)
			);
			setFilteredExpenses(filtered);
		}
	}, [searchTerm, expenses]);

	// Effect for updating date range filtered data
	useEffect(() => {
		if (expenses.length === 0) return;

		filterExpensesByDateRangeAndFilters();
	}, [startDate, endDate, expenses]);

	// Effect for calculating analytics based on date range filtered data
	useEffect(() => {
		if (dateRangeExpenses.length === 0) return;

		// No need to call calculation functions here as they're called directly in filterExpensesByDateRangeAndFilters
		// This prevents unnecessary double calculations
	}, [dateRangeExpenses]);

	const fetchAllExpenses = async () => {
		try {
			setLoading(true);

			// Join expenses with profiles and schools to get user and school information
			const { data, error } = await supabase
				.from("expenses")
				.select(
					`
          *,
          profiles:user_id (
            full_name,
            email
          ),
          schools (
            id,
            name
          ),
          school_visits (
            id,
            schools (
              id,
              name
            )
          )
        `
				)
				.order("date", { ascending: false });

			if (error) throw error;

			// Map the joined data to a flattened structure
			const mappedExpenses = data.map((expense) => {
				// Determine school name - either from direct school relationship or from visit
				let schoolId = null;
				let schoolName = null;

				if (expense.school_id && expense.schools) {
					schoolId = expense.school_id;
					schoolName = expense.schools.name;
				} else if (
					expense.school_visit_id &&
					expense.school_visits &&
					expense.school_visits.schools
				) {
					schoolId = expense.school_visits.schools.id;
					schoolName = expense.school_visits.schools.name;
				}

				return {
					...expense,
					user_full_name: expense.profiles?.full_name || "Unknown User",
					user_email: expense.profiles?.email || "No Email",
					school_id: schoolId,
					school_name: schoolName,
				};
			});

			setExpenses(mappedExpenses);
			setFilteredExpenses(mappedExpenses);

			// Force initial calculation
			const filtered = filterExpensesByDateRange(mappedExpenses);
			setDateRangeExpenses(filtered);
			calculateCategoryBreakdown(filtered);
			calculateDailyAverage(filtered);
			calculateTravelStats(filtered);
		} catch (error) {
			console.error("Error fetching expenses:", error);
		} finally {
			setLoading(false);
		}
	};

	// Helper function for initial date range filtering
	const filterExpensesByDateRange = (expensesData = expenses) => {
		const startTimestamp = startDate.startOf("day");
		const endTimestamp = endDate.endOf("day");

		const filtered = expensesData.filter((expense) => {
			const expenseDate = dayjs(expense.date);
			return (
				expenseDate.isAfter(startTimestamp) &&
				expenseDate.isBefore(endTimestamp)
			);
		});

		return filtered;
	};

	const calculateCategoryBreakdown = (filteredExpenses = dateRangeExpenses) => {
		const categories = {};

		// Use the provided filtered expenses or fallback to dateRangeExpenses
		filteredExpenses.forEach((expense) => {
			const category = expense.expense_type || "Uncategorized";
			if (!categories[category]) {
				categories[category] = 0;
			}
			categories[category] += parseFloat(expense.amount) || 0;
		});

		const categoryArray = Object.keys(categories).map((name) => ({
			name,
			value: categories[name],
		}));

		setCategoryData(categoryArray);
	};

	const calculateDailyAverage = (filteredExpenses = dateRangeExpenses) => {
		// Group expenses by selected period (day, week, month)
		let groupedExpenses = {};
		let format;

		switch (periodFilter) {
			case "week":
				format = "YYYY-[W]ww"; // Year-Week format
				break;
			case "month":
				format = "YYYY-MM"; // Year-Month format
				break;
			default:
				format = "YYYY-MM-DD"; // Year-Month-Day format
		}

		// Use the provided filtered expenses or fallback to dateRangeExpenses
		filteredExpenses.forEach((expense) => {
			const periodKey = dayjs(expense.date).format(format);
			if (!groupedExpenses[periodKey]) {
				groupedExpenses[periodKey] = {
					period: periodKey,
					totalAmount: 0,
					count: 0,
				};
			}
			groupedExpenses[periodKey].totalAmount += parseFloat(expense.amount) || 0;
			groupedExpenses[periodKey].count += 1;
		});

		// Calculate average for each period
		const averagesArray = Object.values(groupedExpenses).map((group) => ({
			period: group.period,
			average: group.count > 0 ? group.totalAmount / group.count : 0,
			totalAmount: group.totalAmount,
			count: group.count,
		}));

		// Sort by period
		const sortedAverages = averagesArray.sort((a, b) =>
			a.period.localeCompare(b.period)
		);

		// Format X-axis labels based on period
		const formattedAverages = sortedAverages.map((item) => {
			let displayName;

			switch (periodFilter) {
				case "week":
					// Extract week number from format YYYY-Www
					const weekMatch = item.period.match(/\d{4}-W(\d{2})/);
					displayName = weekMatch ? `Week ${weekMatch[1]}` : item.period;
					break;
				case "month":
					// Convert YYYY-MM to Month Year
					const date = dayjs(item.period + "-01");
					displayName = date.format("MMM YYYY");
					break;
				default:
					// For daily, keep YYYY-MM-DD but format it nicely
					displayName = dayjs(item.period).format("DD MMM");
			}

			return {
				...item,
				displayName,
			};
		});

		setDailyAverageData(formattedAverages);
	};

	const calculateTravelStats = (filteredExpenses = dateRangeExpenses) => {
		let totalDistance = 0;
		let totalCost = 0;

		// Use the provided filtered expenses or fallback to dateRangeExpenses
		filteredExpenses.forEach((expense) => {
			if (expense.expense_type === "Travel Expense" && expense.distance) {
				totalDistance += parseFloat(expense.distance) || 0;
				totalCost += parseFloat(expense.amount) || 0;
			}
		});

		setTravelStats({
			totalDistance,
			totalCost,
		});
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Handle period change and force recalculation
	const handlePeriodChange = (event) => {
		setPeriodFilter(event.target.value);
		// Force recalculation when period changes
		calculateDailyAverage();
	};

	const handleStartDateChange = (newDate) => {
		setStartDate(newDate);
	};

	const handleEndDateChange = (newDate) => {
		setEndDate(newDate);
	};

	const getExpenseTypeColor = (type) => {
		switch (type) {
			case "Travel Expense":
				return "primary";
			case "Food":
				return "success";
			case "Room Rent":
				return "warning";
			case "Bus Fare":
			case "Taxi Fare":
			case "Train Fare":
				return "info";
			case "Toll":
				return "secondary";
			default:
				return "default";
		}
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
		setPage(0); // Reset to first page when searching
	};

	const openReceipt = (url) => {
		if (url) {
			window.open(url, "_blank");
		}
	};

	if (loading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="200px">
				<CircularProgress />
			</Box>
		);
	}

	// Calculate total amount of filtered expenses
	const totalAmount = filteredExpenses.reduce(
		(sum, expense) => sum + parseFloat(expense.amount || 0),
		0
	);

	// Calculate total amount for date range
	const dateRangeTotalAmount = dateRangeExpenses.reduce(
		(sum, expense) => sum + parseFloat(expense.amount || 0),
		0
	);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Typography variant="h5" component="h1" sx={{ mb: 2 }}>
				Expense List
			</Typography>

			<Box
				sx={{
					mb: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 2,
				}}>
				<TextField
					placeholder="Search expenses..."
					variant="outlined"
					size="small"
					value={searchTerm}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
					sx={{ width: { xs: "100%", sm: "300px" } }}
				/>
				<Typography variant="subtitle1" fontWeight="bold">
					Total: ₹{totalAmount.toFixed(2)}
				</Typography>
				<Button
					variant="outlined"
					size="small"
					startIcon={<DownloadIcon />}
					onClick={exportToExcel}>
					Export to Excel
				</Button>
			</Box>

			<Paper sx={{ overflow: "hidden", mb: 4 }}>
				<TableContainer sx={{ maxHeight: "calc(100vh - 500px)" }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>Date</TableCell>
								<TableCell>User</TableCell>
								<TableCell>School</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Type</TableCell>
								<TableCell align="right">Distance</TableCell>
								<TableCell align="right">Amount (₹)</TableCell>
								<TableCell align="center">Receipt</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredExpenses.length === 0 ? (
								<TableRow>
									<TableCell colSpan={8} align="center">
										No expenses found
									</TableCell>
								</TableRow>
							) : (
								filteredExpenses
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((expense) => (
										<TableRow key={expense.id}>
											<TableCell>
												{dayjs(expense.date).format("DD MMM YYYY")}
											</TableCell>
											<TableCell>
												<div>{expense.user_full_name}</div>
												<div style={{ fontSize: "0.75rem", color: "gray" }}>
													{expense.user_email}
												</div>
											</TableCell>
											<TableCell>{expense.school_name || "-"}</TableCell>
											<TableCell>{expense.description}</TableCell>
											<TableCell>
												<Chip
													label={expense.expense_type}
													color={getExpenseTypeColor(expense.expense_type)}
													size="small"
												/>
											</TableCell>
											<TableCell align="right">
												{expense.distance ? `${expense.distance} km` : "-"}
											</TableCell>
											<TableCell align="right">
												{typeof expense.amount === "number"
													? expense.amount.toFixed(2)
													: parseFloat(expense.amount || 0).toFixed(2)}
											</TableCell>
											<TableCell align="center">
												{expense.receipt_url ? (
													<IconButton
														size="small"
														onClick={() => openReceipt(expense.receipt_url)}
														color="primary">
														<ReceiptIcon fontSize="small" />
													</IconButton>
												) : (
													"-"
												)}
											</TableCell>
										</TableRow>
									))
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					rowsPerPageOptions={[10, 25, 50, 100]}
					component="div"
					count={filteredExpenses.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
				/>
			</Paper>

			{/* Analytics Section */}
			<Typography variant="h6" component="h2" sx={{ mb: 2, mt: 4 }}>
				Expense Analytics
			</Typography>

			{/* Date Range Selector */}
			<Paper sx={{ p: 2, mb: 3 }}>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} md={3}>
						<Typography variant="subtitle1">
							<CalendarMonthIcon
								fontSize="small"
								sx={{ mr: 1, verticalAlign: "middle" }}
							/>
							Date Range Analysis
						</Typography>
					</Grid>
					<Grid item xs={12} md={3}>
						<DatePicker
							label="Start Date"
							value={startDate}
							onChange={handleStartDateChange}
							slotProps={{ textField: { size: "small", fullWidth: true } }}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<DatePicker
							label="End Date"
							value={endDate}
							onChange={handleEndDateChange}
							slotProps={{ textField: { size: "small", fullWidth: true } }}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<FormControl fullWidth size="small">
							<InputLabel>Period</InputLabel>
							<Select
								value={periodFilter}
								label="Period"
								onChange={handlePeriodChange}>
								<MenuItem value="day">Daily</MenuItem>
								<MenuItem value="week">Weekly</MenuItem>
								<MenuItem value="month">Monthly</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={3}>
						<FormControl fullWidth size="small">
							<InputLabel>User</InputLabel>
							<Select
								value={userFilter}
								label="User"
								onChange={handleUserFilterChange}>
								<MenuItem value="">All Users</MenuItem>
								{getUniqueUsers().map((user) => (
									<MenuItem key={user} value={user}>
										{user}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={3}>
						<FormControl fullWidth size="small">
							<InputLabel>School</InputLabel>
							<Select
								value={schoolFilter}
								label="School"
								onChange={handleSchoolFilterChange}
								startAdornment={
									<InputAdornment position="start">
										<SchoolIcon fontSize="small" />
									</InputAdornment>
								}>
								<MenuItem value="">All Schools</MenuItem>
								{schools.map((school) => (
									<MenuItem key={school.id} value={school.id}>
										{school.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>
			</Paper>

			{/* Analytics Cards */}
			<Grid container spacing={3} sx={{ mb: 4 }}>
				{/* Summary Card */}
				<Grid item xs={12} md={4}>
					<Card>
						<CardHeader
							title="Expense Summary"
							subheader={`${startDate.format("DD MMM YYYY")} - ${endDate.format(
								"DD MMM YYYY"
							)}`}
							titleTypographyProps={{ variant: "h6" }}
							sx={{ pb: 0 }}
						/>
						<CardContent>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<Box>
									<Typography variant="subtitle2" color="text.secondary">
										Total Expenses
									</Typography>
									<Typography variant="h4">
										₹{dateRangeTotalAmount.toFixed(2)}
									</Typography>
								</Box>
								<Divider />
								<Box>
									<Typography variant="subtitle2" color="text.secondary">
										Number of Expenses
									</Typography>
									<Typography variant="h5">
										{dateRangeExpenses.length}
									</Typography>
								</Box>
								<Divider />
								<Box>
									<Typography variant="subtitle2" color="text.secondary">
										Average per Expense
									</Typography>
									<Typography variant="h5">
										₹
										{dateRangeExpenses.length > 0
											? (
													dateRangeTotalAmount / dateRangeExpenses.length
											  ).toFixed(2)
											: "0.00"}
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				{/* Travel Expenses Card */}
				<Grid item xs={12} md={4}>
					<Card>
						<CardHeader
							title="Travel Expenses"
							titleTypographyProps={{ variant: "h6" }}
							avatar={<DirectionsCarIcon color="primary" />}
							subheader={
								userFilter && schoolFilter
									? `${userFilter} / ${
											schools.find((s) => s.id === schoolFilter)?.name ||
											"Selected School"
									  }`
									: userFilter
									? userFilter
									: schoolFilter
									? `${
											schools.find((s) => s.id === schoolFilter)?.name ||
											"Selected School"
									  }`
									: "All Users / All Schools"
							}
							sx={{ pb: 0 }}
						/>
						<CardContent>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<Box>
									<Typography variant="subtitle2" color="text.secondary">
										Total Distance
									</Typography>
									<Typography variant="h4">
										{travelStats.totalDistance.toFixed(2)} km
									</Typography>
								</Box>
								<Divider />
								<Box>
									<Typography variant="subtitle2" color="text.secondary">
										Total Travel Cost
									</Typography>
									<Typography variant="h5">
										₹{travelStats.totalCost.toFixed(2)}
									</Typography>
								</Box>
								<Divider />
								<Box>
									<Typography variant="subtitle2" color="text.secondary">
										Average Cost per KM
									</Typography>
									<Typography variant="h5">
										₹
										{travelStats.totalDistance > 0
											? (
													travelStats.totalCost / travelStats.totalDistance
											  ).toFixed(2)
											: "0.00"}
									</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

				{/* Category Breakdown Card */}
				<Grid item xs={12} md={4}>
					<Card>
						<CardHeader
							title="Category Breakdown"
							titleTypographyProps={{ variant: "h6" }}
							avatar={<PieChartIcon color="primary" />}
							subheader={
								userFilter && schoolFilter
									? `${userFilter} / ${
											schools.find((s) => s.id === schoolFilter)?.name ||
											"Selected School"
									  }`
									: userFilter
									? userFilter
									: schoolFilter
									? `${
											schools.find((s) => s.id === schoolFilter)?.name ||
											"Selected School"
									  }`
									: "All Users / All Schools"
							}
							sx={{ pb: 0 }}
						/>
						<CardContent>
							{categoryData.length > 0 ? (
								<ResponsiveContainer width="100%" height={200}>
									<PieChart>
										<Pie
											data={categoryData}
											cx="50%"
											cy="50%"
											labelLine={false}
											label={({ name, percent }) =>
												`${name}: ${(percent * 100).toFixed(0)}%`
											}
											outerRadius={80}
											fill="#8884d8"
											dataKey="value">
											{categoryData.map((entry, index) => (
												<Cell
													key={`cell-${index}`}
													fill={COLORS[index % COLORS.length]}
												/>
											))}
										</Pie>
										<RechartsTooltip
											formatter={(value) => `₹${value.toFixed(2)}`}
										/>
									</PieChart>
								</ResponsiveContainer>
							) : (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: 200,
									}}>
									<Typography variant="body2" color="text.secondary">
										No data available
									</Typography>
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			{/* Charts Row */}
			<Grid container spacing={3}>
				{/* Daily Average Spending */}
				<Grid item xs={12} md={6}>
					<Card>
						<CardHeader
							title={`${
								periodFilter.charAt(0).toUpperCase() + periodFilter.slice(1)
							}ly Average Spending`}
							titleTypographyProps={{ variant: "h6" }}
							avatar={<TrendingUpIcon color="primary" />}
						/>
						<CardContent>
							{dailyAverageData.length > 0 ? (
								<ResponsiveContainer width="100%" height={300}>
									<LineChart
										data={dailyAverageData}
										margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="displayName" />
										<YAxis />
										<RechartsTooltip
											formatter={(value) => `₹${value.toFixed(2)}`}
										/>
										<Legend />
										<Line
											type="monotone"
											dataKey="average"
											name="Average Expense"
											stroke="#8884d8"
											activeDot={{ r: 8 }}
										/>
									</LineChart>
								</ResponsiveContainer>
							) : (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: 300,
									}}>
									<Typography variant="body2" color="text.secondary">
										No data available
									</Typography>
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Category Expenses Bar Chart */}
				<Grid item xs={12} md={6}>
					<Card>
						<CardHeader
							title="Category Expenses"
							titleTypographyProps={{ variant: "h6" }}
						/>
						<CardContent>
							{categoryData.length > 0 ? (
								<ResponsiveContainer width="100%" height={300}>
									<BarChart
										data={categoryData}
										margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="name" />
										<YAxis />
										<RechartsTooltip
											formatter={(value) => `₹${value.toFixed(2)}`}
										/>
										<Legend />
										<Bar dataKey="value" name="Amount" fill="#82ca9d" />
									</BarChart>
								</ResponsiveContainer>
							) : (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: 300,
									}}>
									<Typography variant="body2" color="text.secondary">
										No data available
									</Typography>
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			<Grid container spacing={3} sx={{ mt: 3 }}>
				<Grid item xs={12} md={6}>
					<Card>
						<CardHeader
							title="User Spending Comparison"
							titleTypographyProps={{ variant: "h6" }}
						/>
						<CardContent>
							{dateRangeExpenses.length > 0 ? (
								<ResponsiveContainer width="100%" height={400}>
									<BarChart
										data={getUserSpendingData()}
										margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="name" />
										<YAxis />
										<RechartsTooltip
											formatter={(value) => `₹${value.toFixed(2)}`}
										/>
										<Legend />
										<Bar
											dataKey="amount"
											name="Total Spending"
											fill="#8884d8"
										/>
									</BarChart>
								</ResponsiveContainer>
							) : (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: 400,
									}}>
									<Typography variant="body2" color="text.secondary">
										No data available
									</Typography>
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* School Spending Comparison - New chart */}
				<Grid item xs={12} md={6}>
					<Card>
						<CardHeader
							title="School Spending Comparison"
							titleTypographyProps={{ variant: "h6" }}
							avatar={<SchoolIcon color="primary" />}
						/>
						<CardContent>
							{dateRangeExpenses.length > 0 ? (
								<ResponsiveContainer width="100%" height={400}>
									<BarChart
										data={getSchoolSpendingData()}
										margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="name" />
										<YAxis />
										<RechartsTooltip
											formatter={(value) => `₹${value.toFixed(2)}`}
										/>
										<Legend />
										<Bar
											dataKey="amount"
											name="Total Spending"
											fill="#00C49F"
										/>
									</BarChart>
								</ResponsiveContainer>
							) : (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: 400,
									}}>
									<Typography variant="body2" color="text.secondary">
										No data available
									</Typography>
								</Box>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</LocalizationProvider>
	);
};

export default ExpenseList;
